import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Shield, Database, Cookie, UserCheck, Mail, Lock, Eye, FileSearch } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg text-white">
          <Shield className="w-6 h-6" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-white">Privacy Policy</h2>
          <p className="text-sm text-slate-400">Last updated on Dec 17th 2025</p>
        </div>
      </div>

      <Card className="border-slate-700 bg-slate-800/50 shadow-md">
        <CardContent className="p-6">
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-6 text-slate-300">
              {/* Introduction */}
              <section className="space-y-3">
                <p className="text-sm leading-relaxed">
                  This privacy policy sets out how <strong>MYONK</strong> uses and protects any information that you give MYONK when you visit their website and/or agree to purchase from them.
                </p>
                <p className="text-sm leading-relaxed">
                  MYONK is committed to ensuring that your privacy is protected. Should we ask you to provide certain information by which you can be identified when using this website, then you can be assured that it will only be used in accordance with this privacy statement.
                </p>
                <p className="text-sm leading-relaxed">
                  MYONK may change this policy from time to time by updating this page. You should check this page from time to time to ensure that you adhere to these changes.
                </p>
              </section>

              {/* Information We Collect */}
              <section className="space-y-3">
                <div className="flex items-center gap-2 text-blue-600">
                  <Database className="w-5 h-5" />
                  <h3 className="font-semibold text-lg">Information We Collect</h3>
                </div>
                <p className="text-sm leading-relaxed">We may collect the following information:</p>
                <ul className="list-disc list-inside text-sm space-y-1 ml-4">
                  <li>Name</li>
                  <li>Contact information including email address</li>
                  <li>Demographic information such as postcode, preferences and interests, if required</li>
                  <li>Other information relevant to customer surveys and/or offers</li>
                </ul>
              </section>

              {/* What We Do With Information */}
              <section className="space-y-3">
                <div className="flex items-center gap-2 text-purple-600">
                  <FileSearch className="w-5 h-5" />
                  <h3 className="font-semibold text-lg">What We Do With the Information</h3>
                </div>
                <p className="text-sm leading-relaxed">
                  We require this information to understand your needs and provide you with a better service, and in particular for the following reasons:
                </p>
                <ul className="list-disc list-inside text-sm space-y-2 ml-4">
                  <li>Internal record keeping.</li>
                  <li>We may use the information to improve our products and services.</li>
                  <li>We may periodically send promotional emails about new products, special offers or other information which we think you may find interesting using the email address which you have provided.</li>
                  <li>From time to time, we may also use your information to contact you for market research purposes. We may contact you by email, phone, fax or mail.</li>
                  <li>We may use the information to customise the website according to your interests.</li>
                </ul>
              </section>

              {/* Security */}
              <section className="space-y-3">
                <div className="flex items-center gap-2 text-green-600">
                  <Lock className="w-5 h-5" />
                  <h3 className="font-semibold text-lg">Security</h3>
                </div>
                <p className="text-sm leading-relaxed">
                  We are committed to ensuring that your information is secure. In order to prevent unauthorised access or disclosure we have put in suitable measures.
                </p>
              </section>

              {/* Cookies */}
              <section className="space-y-3">
                <div className="flex items-center gap-2 text-orange-600">
                  <Cookie className="w-5 h-5" />
                  <h3 className="font-semibold text-lg">How We Use Cookies</h3>
                </div>
                <p className="text-sm leading-relaxed">
                  A cookie is a small file which asks permission to be placed on your computer&apos;s hard drive. Once you agree, the file is added and the cookie helps analyze web traffic or lets you know when you visit a particular site. Cookies allow web applications to respond to you as an individual. The web application can tailor its operations to your needs, likes and dislikes by gathering and remembering information about your preferences.
                </p>
                <p className="text-sm leading-relaxed">
                  We use traffic log cookies to identify which pages are being used. This helps us analyze data about webpage traffic and improve our website in order to tailor it to customer needs. We only use this information for statistical analysis purposes and then the data is removed from the system.
                </p>
                <p className="text-sm leading-relaxed">
                  Overall, cookies help us provide you with a better website, by enabling us to monitor which pages you find useful and which you do not. A cookie in no way gives us access to your computer or any information about you, other than the data you choose to share with us.
                </p>
                <p className="text-sm leading-relaxed">
                  You can choose to accept or decline cookies. Most web browsers automatically accept cookies, but you can usually modify your browser setting to decline cookies if you prefer. This may prevent you from taking full advantage of the website.
                </p>
              </section>

              {/* Controlling Personal Information */}
              <section className="space-y-3">
                <div className="flex items-center gap-2 text-teal-600">
                  <UserCheck className="w-5 h-5" />
                  <h3 className="font-semibold text-lg">Controlling Your Personal Information</h3>
                </div>
                <p className="text-sm leading-relaxed">
                  You may choose to restrict the collection or use of your personal information in the following ways:
                </p>
                <ul className="list-disc list-inside text-sm space-y-2 ml-4">
                  <li>Whenever you are asked to fill in a form on the website, look for the box that you can click to indicate that you do not want the information to be used by anybody for direct marketing purposes.</li>
                  <li>If you have previously agreed to us using your personal information for direct marketing purposes, you may change your mind at any time by writing to or emailing us.</li>
                </ul>
                <p className="text-sm leading-relaxed">
                  We will not sell, distribute or lease your personal information to third parties unless we have your permission or are required by law to do so. We may use your personal information to send you promotional information about third parties which we think you may find interesting if you tell us that you wish this to happen.
                </p>
              </section>

              {/* Correction of Information */}
              <section className="space-y-3">
                <div className="flex items-center gap-2 text-indigo-600">
                  <Eye className="w-5 h-5" />
                  <h3 className="font-semibold text-lg">Correction of Information</h3>
                </div>
                <p className="text-sm leading-relaxed">
                  If you believe that any information we are holding on you is incorrect or incomplete, please write to:
                </p>
                <div className="bg-slate-700/50 p-4 rounded-lg text-sm">
                  <p className="font-medium text-white">MYONK</p>
                  <p className="text-slate-300">Bangalore, India</p>
                  <p className="mt-2 flex items-center gap-2 text-teal-400">
                    <Mail className="w-4 h-4" />
                    info@myvenuemate.in
                  </p>
                </div>
                <p className="text-sm leading-relaxed">
                  We will promptly correct any information found to be incorrect.
                </p>
              </section>

              {/* Footer */}
              <div className="pt-6 border-t border-slate-600">
                <p className="text-xs text-slate-400 text-center">
                  © 2026 MYONK. All rights reserved.
                </p>
                <p className="text-xs text-slate-500 text-center mt-1">
                  Your privacy is important to us. By using MyVenueMate, you agree to this Privacy Policy.
                </p>
              </div>
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
      
      {/* Back to Home */}
      <div className="text-center pt-4">
        <button 
          onClick={() => window.location.href = '/'}
          className="px-4 py-2 border border-slate-600 text-slate-300 rounded-md hover:bg-slate-700/50 transition-colors"
        >
          Back to Home
        </button>
      </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
